import {Component, computed, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Option} from '../../../campaña/components/campana-select/campana-select-component';
import {CampaignStoreService} from '../../../campaña/services/campaign-store.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FiltroSelectComponent} from './components/FiltroSelectComponent/filtro-select.component';
import {DemographicsOptionsService} from './service/demographics-options.service';
import {distinctUntilChanged, filter, shareReplay, switchMap, take, tap} from 'rxjs';
import {ClientMetadataService} from '@/app/shared/services/metadata/client-metadata-service';
import {AnalysisDemographicFilters} from '../../infrastructure/analysis.api.types';
import {ReportFiltersStateService} from './service/report-filters-state.service';
import {ThemeService} from '@/app/core/services/theme/theme.service';
import {SnackbarService} from '@/app/shared/services/snackbar.service';

export type AnalysisMode =
  | 'final-assessments'
  | 'category-scores'
  | 'domain-scores'
  | 'dimension-scores';
export type DemographicKey =
  | 'gender' | 'ageRange' | 'department' | 'educationLevel' | 'position'
  | 'employeeType' | 'seniority' | 'contractType' | 'maritalStatus'
  | 'educationStatus' | 'occupation' | 'workSchedule'
  | 'hasShiftRotation' | 'totalWorkExperience';

@Component({
  selector: 'app-reporte-filtros',
  standalone: true,
  imports: [CommonModule, FiltroSelectComponent],
  templateUrl: './reporte-filtros.component.html',
  styleUrls: ['./reporte-filtros.component.scss'],
})
export class ReporteFiltrosComponent implements OnInit {
  @Input() disableCategories = false;

  groupByOptions: { key: DemographicKey; label: string }[] = [
    {key: 'gender', label: 'Género'},
    {key: 'ageRange', label: 'Rango de edad'},
    {key: 'department', label: 'Área'},
    {key: 'position', label: 'Puesto'},
    {key: 'employeeType', label: 'Tipo de empleado'},
    {key: 'seniority', label: 'Antigüedad en puesto'},
    {key: 'contractType', label: 'Tipo de contrato'},
    {key: 'maritalStatus', label: 'Estado civil'},
    {key: 'educationLevel', label: 'Nivel educativo'},
    {key: 'educationStatus', label: 'Estatus educativo'},
    {key: 'occupation', label: 'Ocupación'},
    {key: 'workSchedule', label: 'Horario de trabajo'},
    {key: 'totalWorkExperience', label: 'Experiencia laboral total'},
    {key: 'hasShiftRotation', label: 'Rotación de turno'},
  ];
  analysisMode: AnalysisMode | null = null;
  groupBy = new Set<DemographicKey>();
  campaignOptions: Option[] = [];
  selectedCampaignOption: Option | undefined;
  genderOptions: Option[] = [];
  ageRangeOptions: Option[] = [];
  departmentOptions: Option[] = [];
  departmentsData: any[] = [];
  educationLevelOptions: Option[] = [];
  positionOptions: Option[] = [];
  employeeTypeOptions: Option[] = [];
  seniorityOptions: Option[] = [];
  contractTypeOptions: Option[] = [];
  maritalStatusOptions: Option[] = [];
  educationStatusOptions: Option[] = [];
  occupationOptions: Option[] = [];
  workScheduleOptions: Option[] = [];
  totalWorkExperienceOptions: Option[] = [];
  hasShiftRotationOptions: Option[] = [];
  loadingDepartment = false;
  loadingOccupation = false;
  metaError: string | null = null;
  filters: { [key in DemographicKey]?: Option } = {};
  private hasShownNoOccupationsSnackbar = false;
  protected reportFiltersComponent = computed(() => this.reportContent()?.reportFiltersComponent)
  private themeService = inject(ThemeService);
  protected themeContent = this.themeService.themeContent;
  protected reportContent = computed(() => this.themeContent()?.reports);
  private store = inject(CampaignStoreService);
  private destroyRef = inject(DestroyRef);
  private demographics = inject(DemographicsOptionsService);
  private meta = inject(ClientMetadataService);
  private stateService = inject(ReportFiltersStateService);
  private snackbarService = inject(SnackbarService);



  get occupationPlaceholder(): string {
    if (this.loadingOccupation) return 'Cargando ocupación…';
    if (!this.filters.department?.value) return 'Ocupación';
    if (this.occupationOptions.length === 1 && this.occupationOptions[0].label === 'No hay ocupaciones disponibles para este departamento') {
      return 'No hay ocupaciones';
    }
    return 'Ocupación';
  }

  get hasActiveFilters(): boolean {
    return !!this.analysisMode; // solo habilitado si hay mode que es el tipo de reporte
  }

  setMode(m: AnalysisMode) {
    this.analysisMode = this.analysisMode === m ? null : m;
  }

  toggleGroupBy(k: DemographicKey) {
    if (this.groupBy.has(k)) this.groupBy.delete(k);
    else this.groupBy.add(k);
  }

  clearGroupBy() {
    this.groupBy.clear();
  }

  clearGroupByFilters() {
    this.filters = {};
    this.updateOccupationOptions();
  }

  onDepartmentChange(selectedDepartment: Option | Option[] | undefined) {
    this.filters.department = Array.isArray(selectedDepartment)
      ? selectedDepartment[0]
      : selectedDepartment;
    this.filters.occupation = undefined;
    this.hasShownNoOccupationsSnackbar = false; // Reset flag when department changes
    this.updateOccupationOptions();
  }

  private updateOccupationOptions() {
    if (!this.filters.department?.value) {
      this.occupationOptions = [
        {
          value: '',
          label: 'Seleccione primero un departamento',
        },
      ];
      return;
    }

    const selectedDept = this.departmentsData.find(
      (dept) => dept.name === this.filters.department?.value
    );

    if (
      selectedDept &&
      selectedDept.occupations &&
      selectedDept.occupations.length > 0
    ) {
      this.occupationOptions = selectedDept.occupations.map((occ: any) => ({
        value: occ.name,
        label: occ.name,
      }));
    } else {
      this.occupationOptions = [
        {
          value: '',
          label: 'No hay ocupaciones disponibles para este departamento',
        },
      ];

      // Show snackbar only once per department selection
      if (!this.hasShownNoOccupationsSnackbar) {
        this.snackbarService.showInfo('No tiene ocupaciones creadas');
        this.hasShownNoOccupationsSnackbar = true;
      }
    }

    this.loadingOccupation = false;
  }

  ngOnInit(): void {
    this.store.campaignsOptions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opts) => (this.campaignOptions = opts));

    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opt) => (this.selectedCampaignOption = opt ?? undefined));

    this.genderOptions = this.demographics.getGender();
    this.ageRangeOptions = this.demographics.getAgeRange();
    this.departmentOptions = this.demographics.getDepartment();
    this.educationLevelOptions = this.demographics.getEducationLevel();
    this.positionOptions = this.demographics.getPosition();
    this.employeeTypeOptions = this.demographics.getEmployeeType();
    this.seniorityOptions = this.demographics.getSeniority();
    this.contractTypeOptions = this.demographics.getContractType();
    this.maritalStatusOptions = this.demographics.getMaritalStatus();
    this.educationStatusOptions = this.demographics.getEducationStatus();
    this.occupationOptions = this.demographics.getOccupation();
    this.workScheduleOptions = this.demographics.getWorkSchedule();
    this.totalWorkExperienceOptions =
      this.demographics.getTotalWorkExperience();
    this.hasShiftRotationOptions = this.demographics.getHasShiftRotation();

    const campaignClient$ = this.store.selectedCampaignClient$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(campaign => !!campaign),
      distinctUntilChanged((previous, current) =>
        previous?.publicId === current?.publicId
      ),
      shareReplay(1)
    );

    campaignClient$.pipe(take(1)).subscribe((campaign) => {
      if (!campaign) return;
      const savedState = this.stateService.getState(campaign.publicId);
      if (savedState) {
        this.filters = savedState.filters;
        this.groupBy = new Set(savedState.groupBy);
        this.analysisMode = savedState.analysisMode ?? null;
      }
    });

    campaignClient$
      .pipe(
        tap(() => {
          this.loadingDepartment = true;
          this.loadingOccupation = true;
          this.metaError = null;
          this.departmentOptions = [];
          this.occupationOptions = [];
        }),
        switchMap((camp) => this.meta.getClientMetadata(camp!.publicId))
      )
      .subscribe({
        next: (md) => {
          const toOptions = (list: unknown[]): Option[] =>
            (list ?? []).map((it: any) => {
              const text: string =
                typeof it === 'string'
                  ? it
                  : it?.name ?? it?.label ?? it?.value ?? String(it);
              return { value: text, label: text };
            });

          this.departmentsData = (md as any).departments || [];

          const departmentOptions = toOptions(this.departmentsData);
          this.departmentOptions = departmentOptions;
          this.demographics.setDepartments(departmentOptions);

          const allOccupations = (this.departmentsData ?? [])
            .flatMap((dept: any) => dept.occupations ?? [])
            .map((occ: any) => occ.name);
          const uniqueOccupations = [...new Set(allOccupations)];
          const occupationOptions = uniqueOccupations.map(name => ({value: String(name), label: String(name)}));
          this.demographics.setOccupations(occupationOptions);

          this.updateOccupationOptions();

          this.loadingDepartment = false;
          this.loadingOccupation = false;
        },
        error: (err: Error) => {
          this.metaError =
            err.message || 'No se pudieron cargar los metadatos.';
          this.loadingDepartment = false;
          this.loadingOccupation = false;
          this.departmentOptions = [];
          this.occupationOptions = [];
        },
      });
  }

  onCampaignSelect(opt: Option) {
    this.reset();
    this.store.setSelectedCampaign(opt);
  }

  public getPayload(): {
    facets: DemographicKey[];
    filters: Partial<AnalysisDemographicFilters>;
    analysisMode: AnalysisMode;
  } {
    const campaignId = this.selectedCampaignOption?.value;
    if (campaignId) {
      this.stateService.setState(String(campaignId), {
        filters: this.filters,
        groupBy: Array.from(this.groupBy),
        ...(this.analysisMode ? {analysisMode: this.analysisMode} : {}),
      });
    }

    const out: Partial<AnalysisDemographicFilters> = {
      gender: this.val(this.filters.gender),
      ageRange: this.val(this.filters.ageRange),
      department: this.val(this.filters.department),
      educationLevel: this.val(this.filters.educationLevel),
      position: this.val(this.filters.position),
      employeeType: this.val(this.filters.employeeType),
      seniority: this.val(this.filters.seniority),
      contractType: this.val(this.filters.contractType),
      maritalStatus: this.val(this.filters.maritalStatus),
      educationStatus: this.val(this.filters.educationStatus),
      occupation: this.val(this.filters.occupation),
      workSchedule: this.val(this.filters.workSchedule),
      totalWorkExperience: this.val(this.filters.totalWorkExperience),
      hasShiftRotation: this.valBool(this.filters.hasShiftRotation),
    };

    const filters = {} as Partial<AnalysisDemographicFilters>;
    (Object.keys(out) as Array<keyof AnalysisDemographicFilters>).forEach((k) => {
      const value = out[k];
      if (value !== undefined && value !== null && value !== '') {
        filters[k] = value as any;
      }
    });

    return {
      facets: Array.from(this.groupBy),
      filters,
      analysisMode: this.analysisMode!,
    };
  }

  public reset() {
    this.filters = {};
    this.groupBy.clear();
    this.stateService.clearState();
  }

  // Helpers de conversión
  private val(opt?: Option): string | undefined {
    return opt?.value ? String(opt.value) : undefined;
  }

  private valBool(opt?: Option): boolean | undefined {
    if (!opt || opt.value === undefined || opt.value === null) return undefined;
    const v = String(opt.value).toLowerCase();
    if (['true', '1', 'sí', 'si', 'yes'].includes(v)) return true;
    if (['false', '0', 'no'].includes(v)) return false;
    return undefined;
  }
}
